import {
  GetStaticPathsResult,
  GetStaticPropsContext,
} from 'next'
import {
  SharedSessionProvider,
  useSession,
} from '@devbookhq/react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMediaQuery } from 'react-responsive'

import {
  Example,
  getDefaults,
  getExampleData,
  getExamples,
  getTOC,
} from 'utils/examplesContent'
import {
  Handler as ExampleContentHandler,
  Desktop as ExampleDesktopContent,
  Mobile as ExampleMobileContent,
} from 'components/ExampleContent'
import { LanguageClientsProvider } from 'utils/useLanguageClients'
import { TOCPage } from 'components/TOCSidebar/TOCPage'
import {
  dbSchema,
  defaultDBProvider,
} from 'components/Code'
import { rootdir } from 'utils/constants'
import useOnUserAction from 'utils/useOnUserAction'

export function getStaticPaths(): GetStaticPathsResult<{ exampleID: string; categoryID: string; subcategoryID: string }> {
  const examples = getExamples()

  return {
    paths: examples.map(e => ({ params: e })),
    fallback: false,
  }
}

export async function getStaticProps(context: GetStaticPropsContext<{ exampleID: string; subcategoryID: string; categoryID: string }>): Promise<{ props: Props }> {
  const categoryID = context.params?.categoryID
  const subcategoryID = context.params?.subcategoryID
  const id = context.params?.exampleID

  if (typeof id !== 'string' || typeof categoryID !== 'string' || typeof subcategoryID !== 'string') {
    throw new Error('Invalid params')
  }

  const defaults = getDefaults()

  const example = getExampleData(
    categoryID,
    subcategoryID,
    id,
    defaults
  )
  const toc = getTOC('/examples')

  const category = toc.find(c => c.categoryID === example.categoryID)!
  const subcategory = category?.pages.find(s => s.subcategoryID === example.subcategoryID)!

  return {
    props: {
      example,
      toc,
      category,
      subcategory,
    },
  }
}

const inactivityTimeout = 3 * 60 * 1000

export interface Props {
  example: Example
  category: TOCPage
  subcategory: TOCPage
  toc: TOCPage[]
}

function ExamplePage({
  example: initExample,
  category,
  subcategory,
}: Props) {
  const contentRef = useRef<ExampleContentHandler>(null)
  const [isMounted, setIsMounted] = useState(false)

  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const example = useMemo(() => {
    const schema = initExample.tabs.find(t => t.name === dbSchema)
    if (schema) {
      schema.code = schema.code.replace('<<DATABASE_PROVIDER>>', defaultDBProvider)
    }
    return initExample
  },
  [initExample])

  const sessionHandle = useSession({
    codeSnippetID: example.environmentID,
    debug: process.env.NODE_ENV === 'development',
    inactivityTimeout,
  })
  const {
    session,
    refresh: refreshSession,
  } = sessionHandle

  useOnUserAction({ onAction: refreshSession })

  const handleCodeChange = useCallback(async (code: string, file: string) => {
    session?.filesystem?.write(`${rootdir}/${file}`, code)
  }, [session?.filesystem])

  const handleWakeUpSessionClick = useCallback(() => {
    refreshSession()
  }, [refreshSession])

  useEffect(function mount() {
    setIsMounted(true)
  }, [])

  useEffect(function initFiles() {
    example.tabs.forEach(t => handleCodeChange(t.code, t.name))
  }, [handleCodeChange, example.tabs])

  const runCode = useCallback(async () => {
    await refreshSession()

    contentRef.current?.terminal()?.clear()

    // This writes the command as a string to the persistent interactive terminal so user can see what is being executed
    // and then resets the position of the caret.
    await contentRef.current?.terminal()?.write(example.runCmd+'\n\x1B[1G')

    // This creates a new terminal process on the backend and redirects the stdout/stdin to the terminal emulator on the frontend.
    // After the command finishes the stdout/stdin from the persistent interactive terminal is redirected back to the terminal emulator.
    await contentRef.current?.terminal()?.runCmd(example.runCmd)
  }, [example, refreshSession])

  const stopCode = useCallback(() => {
    contentRef.current?.terminal()?.stopCmd()
  }, [])

  useEffect(function autorun() {
    if (!session?.terminal) return

    // The terminal component can be uninitialized right now so we hackishly put the call to therminal at the end of the stack.
    setTimeout(async () => {
      contentRef.current?.terminal()?.clear()

      // This writes the command as a string to the persistent interactive terminal so user can see what is being executed
      // and then resets the position of the caret.
      await contentRef.current?.terminal()?.write(example.runCmd+'\n\x1B[1G')

      // This creates a new terminal process on the backend and redirects the stdout/stdin to the terminal emulator on the frontend.
      // After the command finishes the stdout/stdin from the persistent interactive terminal is redirected back to the terminal emulator.
      await contentRef.current?.terminal()?.runCmd(example.runCmd)
    }, 0)

    return () => {
      stopCode()
    }
  }, [
    stopCode,
    example,
    session?.terminal,
  ])

  if (!isMounted) return null

  return (
    <SharedSessionProvider session={sessionHandle}>
      <LanguageClientsProvider session={sessionHandle.session}>
        {isMobile &&
          <ExampleMobileContent
            category={category}
            example={example}
            ref={contentRef}
            subcategory={subcategory}
            onCodeChange={handleCodeChange}
            onRunClick={runCode}
            onStopClick={stopCode}
            onWakeUpSessionClick={handleWakeUpSessionClick}
          />
        }
        {!isMobile && (
          <ExampleDesktopContent
            category={category}
            example={example}
            ref={contentRef}
            subcategory={subcategory}
            onCodeChange={handleCodeChange}
            onRunClick={runCode}
            onStopClick={stopCode}
            onWakeUpSessionClick={handleWakeUpSessionClick}
          />
        )}
      </LanguageClientsProvider>
    </SharedSessionProvider>
  )
}

export default ExamplePage
